import type { Size, Transform, Vec2 } from '@/types';
import { applyMatrix, invert, matrixFromTransform, normalizeAngle } from '@/core/geometry/transform';

/** The eight resize handles, named by compass direction. */
export type ResizeHandle = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';
export const RESIZE_HANDLES: readonly ResizeHandle[] = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];

export interface ResizeStart {
  readonly position: Vec2;
  readonly size: Size;
  readonly rotation: number;
  readonly scale: Vec2;
}

export interface ResizeResult {
  readonly position: Vec2;
  readonly size: Size;
}

/**
 * Resizes from a handle. Deltas are measured in the object's own rotated frame so a
 * rotated object grows along its edges, and the opposite edge stays fixed in world
 * space (the box centre shifts, which is why position changes too).
 *
 * `startWorld`/`currentWorld` are in the parent's frame (world for roots).
 */
export const resizeFromHandle = (
  start: ResizeStart,
  handle: ResizeHandle,
  startWorld: Vec2,
  currentWorld: Vec2,
  minSize: Size,
  keepAspect = false,
): ResizeResult => {
  // Delta in the unrotated local frame (undo rotation and scale only).
  const rot = matrixFromTransform({ position: { x: 0, y: 0 }, rotation: start.rotation, scale: start.scale });
  const inv = invert(rot);
  const dWorld = { x: currentWorld.x - startWorld.x, y: currentWorld.y - startWorld.y };
  const d = inv ? applyMatrix(inv, dWorld) : dWorld;

  const west = handle.includes('w');
  const east = handle.includes('e');
  const north = handle.includes('n');
  const south = handle.includes('s');

  let width = start.size.width + (east ? d.x : 0) - (west ? d.x : 0);
  let height = start.size.height + (south ? d.y : 0) - (north ? d.y : 0);

  if (keepAspect && (east || west) && (north || south)) {
    const aspect = start.size.width / start.size.height;
    if (Math.abs(width - start.size.width) > Math.abs(height - start.size.height) * aspect) height = width / aspect;
    else width = height * aspect;
  }
  width = Math.max(minSize.width, width);
  height = Math.max(minSize.height, height);

  // The fixed edge/corner must not move: compute the local shift of the box origin,
  // then rotate it back into the parent frame. Because rotation is about the centre,
  // the centre moves by half the size change (in local frame) plus the origin shift.
  const dw = width - start.size.width;
  const dh = height - start.size.height;
  const originShiftLocal = { x: west ? -dw : 0, y: north ? -dh : 0 };
  // New centre in local (unrotated) coordinates relative to old centre:
  const centreShiftLocal = { x: originShiftLocal.x + dw / 2, y: originShiftLocal.y + dh / 2 };
  const centreShift = applyMatrix(rot, centreShiftLocal);
  const oldCentre = { x: start.position.x + start.size.width / 2, y: start.position.y + start.size.height / 2 };
  const newCentre = { x: oldCentre.x + centreShift.x, y: oldCentre.y + centreShift.y };
  return {
    size: { width, height },
    position: { x: newCentre.x - width / 2, y: newCentre.y - height / 2 },
  };
};

/**
 * Rotation for a rotate handle drag: the angle change between the vector from the
 * box centre to the pointer at start and now, added to the starting rotation.
 * With `snap` (radians) the result is quantised, e.g. Math.PI / 12 for 15° steps.
 */
export const rotationFromDrag = (
  centre: Vec2,
  startRotation: number,
  startWorld: Vec2,
  currentWorld: Vec2,
  snap: number | null = null,
): number => {
  const a0 = Math.atan2(startWorld.y - centre.y, startWorld.x - centre.x);
  const a1 = Math.atan2(currentWorld.y - centre.y, currentWorld.x - centre.x);
  let next = startRotation + (a1 - a0);
  if (snap !== null && snap > 0) next = Math.round(next / snap) * snap;
  return normalizeAngle(next);
};

/** Centre of a transform's content box in the parent frame. */
export const boxCentre = (t: Transform, size: Size): Vec2 => ({
  x: t.position.x + size.width / 2,
  y: t.position.y + size.height / 2,
});
